/**
 * Serviço de Campanhas.
 *
 * "Campanha" é o nome na interface; no servidor a entidade continua sendo
 * `Disparo`. Por isso a rota é `/disparos` e as CHAVES de objeto trocadas com a
 * API (`idDisparo`, `disparo`, `creationStatus`) mantêm os nomes de lá —
 * renomeá-las quebra a desserialização em silêncio. Só os nomes de tipo e de
 * método, que são nossos, falam campanha.
 */
import { IdCampanha, IdInbox } from '../common-types'
import { GenericService } from '../generic-service'
import { IMetaParams } from '../templates-wpp-meta/meta-wpp-template.service'

export type TipoCampanhaMensagem = 'TEXTO' | 'ANEXO' | 'TEMPLATE' | 'META_TEMPLATE' | 'BOT'
export type TipoAgendamento = TipoCampanhaMensagem
export type StatusAgendamento = 'AGUARDANDO' | 'ENVIADO' | 'FALHA'

export type CreateCampanha = {
    idInbox: IdInbox
    nome: string
    descricao: string
    callback?: string
    /** Literal no fuso do inbox (yyyy-MM-ddTHH:mm, sem offset). Nulo/ausente = início imediato. */
    dataInicio?: string | null
}

export type UpdateCampanha = Partial<CreateCampanha> & { id: IdCampanha }

export type ResponseCampanha = {
    id: IdCampanha
    idUsuario: string | null
    idInbox: IdInbox
    nome: string
    descricao: string
    dataCriacao: string
    dataInicio: string | null
    del: boolean
    callback: string | null
    totalMensagens: number
    mensagensEnviadas: number
    mensagensFalha: number
    /**
     * Atendimentos ainda abertos que esta campanha criou ou tocou.
     *
     * Vem contado do servidor porque o bloco `disparo` do atendimento traz só o
     * vínculo MAIS RECENTE: derivar daqui esconderia a campanha A de um
     * atendimento que A tocou e B tocou depois. Ausente em servidor anterior a
     * esta feature — trate como 0.
     */
    atendimentosAtivos: number
    /**
     * Instante da tentativa de envio mais recente da campanha — conta tanto o que
     * saiu (ENVIADO) quanto o que falhou (FALHA), porque falha também consumiu o
     * envio. Null = nenhuma tentativa aconteceu ainda (só AGUARDANDO).
     * É a data de onde parte a carência de 30 dias da exclusão.
     */
    dataUltimoEnvio: string | null
    /**
     * Se a campanha pode ser excluída agora: sem nenhum envio cadastrado, ou já
     * passados 30 dias da última tentativa (envio ou falha).
     *
     * A regra é decidida no servidor e revalidada no DELETE — aqui serve só para
     * mostrar ou esconder o botão. Ausente em servidor anterior a esta feature —
     * trate como `false` (esconde o botão em vez de oferecer uma exclusão que o
     * servidor recusaria).
     */
    podeExcluir: boolean
}

export type CreateCampanhaMensagem = {
    tipo: TipoCampanhaMensagem
    mensagem?: string
    legenda?: string
    url?: string
    idTemplate?: string
    idMetaTemplate?: string
    metaTemplateParams?: IMetaParams
    idBot?: string
    idContato?: string
    codigo?: string
    idsContatos?: string[]
    codigos?: string[]
    idExterno?: string
    emLote?: boolean
    todosContatos?: boolean
    idEtiqueta?: string
}

/**
 * Conteúdo da campanha congelado na importação de planilha: é ele que o backend grava em
 * cada envio criado a partir das linhas válidas. É o subconjunto "mensagem" de
 * `CreateCampanhaMensagem` — quem define os destinatários é a planilha.
 */
export type CampanhaImportacaoMensagem = {
    tipo: TipoCampanhaMensagem
    mensagem?: string
    legenda?: string
    url?: string
    idTemplate?: string
    idMetaTemplate?: string
    metaTemplateParams?: IMetaParams
    idBot?: string
}

export type ImportarContatosCampanhaDto = {
    file: File
    sobreescreverDados: boolean
    mensagem: CampanhaImportacaoMensagem
}

/**
 * Criação de campanha a partir de um template Meta (`POST /disparos/meta-template`).
 * Não tem `idInbox`: o inbox é o do template. Os seletores de destinatário são
 * opcionais — sem nenhum, cria só a campanha.
 */
export type CreateCampanhaMetaTemplate = {
    idMetaTemplate: string
    nome: string
    descricao: string
    callback?: string
    dataInicio?: string | null
    metaTemplateParams?: IMetaParams
    idContato?: string
    codigo?: string
    idsContatos?: string[]
    codigos?: string[]
    todosContatos?: boolean
    idEtiqueta?: string
}

/** Importação de planilha vinculada a uma campanha (mesma forma de `ResponseContatoImportacaoDto`). */
export type ResponseCampanhaImportacao = {
    id: string
    nomeArquivo?: string
    url: string
    sobreescreverDados: boolean
    dataCriacao: string
    dataInicioProcessamento?: string
    dataTerminoProcessamento?: string
    linhasJaProcessadas: number
    totalLinhas?: number
    status: string
    idConta: string
    idDisparo: string | null
    qtdErros?: number
}

export type ResponseCampanhaMensagem = {
    id: string
    tipo: TipoAgendamento
    status: StatusAgendamento
    datetimeEnvio: string
    /** Instante real do envio (status ENVIADO); null em linhas históricas. */
    datetimeEnviado: string | null
    mensagem: string | null
    legenda: string | null
    url: string | null
    idDisparo: string | null
    idContato: string | null
    /**
     * Diagnóstico do envio (feature meta-limites-qualidade-disparos). `erroClasse`
     * distingue o que passa sozinho (represado, segue AGUARDANDO) do que é definitivo.
     */
    erroClasse: string | null
    erroCodigo: number | null
    erroMensagem: string | null
    tentativas: number
    /** Preenchido quando o envio está represado esperando limite/backoff. */
    proximaTentativaEm: string | null
    contato: { id: string; nome: string; codigo: string; imagem: string | null } | null
    inbox: { id: string; nome: string } | null
    usuario: { id: string; nome: string } | null
    bot: { id: string; nome: string } | null
    /**
     * Atendimento gerado ou tocado por este envio (feature disparo-atendimento-vinculo).
     * Nulo para envio a grupo, envio que ainda não saiu, ou envio anterior à feature.
     */
    idAtendimento: string | null
}

export class CampanhasService extends GenericService<CreateCampanha, UpdateCampanha, ResponseCampanha> {
    constructor() { super('disparos') }

    async getMensagens(idCampanha: IdCampanha): Promise<ResponseCampanhaMensagem[]> {
        return await this.f.get(`/${idCampanha}/mensagens`, undefined, { resolve404AsValue: true, resolve404As: [] }) as ResponseCampanhaMensagem[]
    }

    async createMensagem(idCampanha: IdCampanha, dto: CreateCampanhaMensagem): Promise<unknown> {
        return await this.f.post(`/${idCampanha}/mensagens`, dto)
    }

    /**
     * Cria a campanha a partir de um template oficial da Meta. O inbox vem do
     * próprio template — por isso `idInbox` não entra no corpo. Sem destinatário,
     * a campanha nasce vazia e `mensagens` volta nulo.
     */
    async criarComMetaTemplate(dto: CreateCampanhaMetaTemplate): Promise<{ disparo: ResponseCampanha; mensagens: unknown }> {
        return await this.f.post('/meta-template', dto) as { disparo: ResponseCampanha; mensagens: unknown }
    }

    /**
     * Sobe a planilha para dentro da campanha. `mensagem` vai como objeto: o FetchUtil
     * serializa objetos em JSON ao montar o FormData, que é o formato esperado pela rota
     * (o corpo é multipart por causa do arquivo).
     */
    async importarContatos(idCampanha: IdCampanha, dto: ImportarContatosCampanhaDto): Promise<{ msg: string; idImportacao: string }> {
        return await this.f.post(`/${idCampanha}/importacoes`, dto, { formdata: true, jsonPayload: false }) as { msg: string; idImportacao: string }
    }

    async getImportacoes(idCampanha: IdCampanha): Promise<ResponseCampanhaImportacao[]> {
        return await this.f.get(`/${idCampanha}/importacoes`, undefined, { resolve404AsValue: true, resolve404As: [] }) as ResponseCampanhaImportacao[]
    }
}
